import type { NodePropertySchema } from '../../../types/node-properties';

/**
 * Redis Node Schema
 * Based on n8n's Redis node
 *
 * Supports:
 * - Key-value operations (Get, Set, Delete, Increment)
 * - List operations (Push, Pop, List Length)
 * - Utility operations (Keys, Info)
 * - Pub/Sub (Publish)
 */
const schema: NodePropertySchema = {
  nodeType: 'redis',
  version: '1.0.0',

  properties: [
    // ==================================================================
    // CREDENTIAL SELECTOR
    // ==================================================================
    {
      key: 'credentialId',
      label: 'Credential',
      type: 'credential',
      required: true,
      description: 'Select the Redis credential to use',
      credentialTypes: [{ name: 'redis', required: true }],
    },

    // ==================================================================
    // OPERATION SELECTOR
    // ==================================================================
    {
      key: 'operation',
      label: 'Operation',
      type: 'options',
      required: true,
      default: 'get',
      options: [
        { name: 'Delete', value: 'delete', description: 'Delete a key from Redis' },
        { name: 'Get', value: 'get', description: 'Get the value of a key' },
        { name: 'Set', value: 'set', description: 'Set the value of a key' },
        { name: 'Increment', value: 'increment', description: 'Increment a numeric key by 1' },
        { name: 'Keys', value: 'keys', description: 'Get all keys matching a pattern' },
        { name: 'Info', value: 'info', description: 'Get Redis server information' },
        { name: 'Push', value: 'push', description: 'Push a value to a list' },
        { name: 'Pop', value: 'pop', description: 'Pop a value from a list' },
        { name: 'List Length', value: 'listLength', description: 'Get the length of a list' },
        { name: 'Publish', value: 'publish', description: 'Publish a message to a channel' },
        { name: 'Bulk Set', value: 'bulkSet', description: 'Set multiple keys from an array' },
        { name: 'Multi Get', value: 'multiGet', description: 'Get values for multiple keys' },
        { name: 'Key Exists', value: 'keyExists', description: 'Check if a key exists' },
        { name: 'Database Size', value: 'dbSize', description: 'Get the number of keys in the database' },
        { name: 'Flush Database', value: 'flushDb', description: 'Delete all keys in the current database' },
      ],
      description: 'The operation to perform',
    },

    // ==================================================================
    // DELETE OPERATION
    // ==================================================================
    {
      key: 'key',
      label: 'Key',
      type: 'string',
      required: true,
      placeholder: 'user:123',
      description: 'The key to delete',
      displayOptions: {
        show: {
          operation: ['delete'],
        },
      },
      validation: [{ type: 'required', message: 'Key is required' }],
    },

    // ==================================================================
    // GET OPERATION
    // ==================================================================
    {
      key: 'key',
      label: 'Key',
      type: 'string',
      required: true,
      placeholder: 'user:123',
      description: 'The key to retrieve',
      displayOptions: {
        show: {
          operation: ['get'],
        },
      },
      validation: [{ type: 'required', message: 'Key is required' }],
      modeToggle: {
        enabled: true,
        defaultMode: 'text',
        loadOptions: {
          method: 'getKeys',
          cacheResults: true,
          cacheTtl: 30000,
        },
        textModeLabel: 'Expression',
        dataModeLabel: 'Browse',
      },
    },
    {
      key: 'keyType',
      label: 'Key Type',
      type: 'options',
      default: 'automatic',
      options: [
        { name: 'Automatic', value: 'automatic', description: 'Auto-detect the key type' },
        { name: 'String', value: 'string', description: 'Simple string value' },
        { name: 'Hash', value: 'hash', description: 'Hash (field-value pairs)' },
        { name: 'List', value: 'list', description: 'List (ordered collection)' },
        { name: 'Set', value: 'set', description: 'Set (unique values)' },
      ],
      description: 'The type of value stored at this key',
      displayOptions: {
        show: {
          operation: ['get'],
        },
      },
    },
    {
      key: 'propertyName',
      label: 'Property Name',
      type: 'string',
      default: 'value',
      placeholder: 'value',
      description: 'The output property name to assign the value to',
      displayOptions: {
        show: {
          operation: ['get', 'pop'],
          dotNotation: [true],
        },
      },
    },
    {
      key: 'dotNotation',
      label: 'Dot Notation',
      type: 'boolean',
      default: false,
      description: 'Use dot notation for the output property name',
      displayOptions: {
        show: {
          operation: ['get', 'pop'],
        },
      },
    },

    // ==================================================================
    // SET OPERATION
    // ==================================================================
    {
      key: 'key',
      label: 'Key',
      type: 'string',
      required: true,
      placeholder: 'user:123',
      description: 'The key to set',
      displayOptions: {
        show: {
          operation: ['set'],
        },
      },
      validation: [{ type: 'required', message: 'Key is required' }],
    },
    {
      key: 'value',
      label: 'Value',
      type: 'string',
      required: true,
      placeholder: '{{ $node["NodeName"].string }}',
      description: 'The value to store',
      displayOptions: {
        show: {
          operation: ['set'],
        },
      },
      typeOptions: {
        rows: 4,
      },
    },
    {
      key: 'keyType',
      label: 'Key Type',
      type: 'options',
      default: 'string',
      options: [
        { name: 'String', value: 'string', description: 'Simple string value' },
        { name: 'Hash', value: 'hash', description: 'Hash (provide JSON object)' },
        { name: 'List', value: 'list', description: 'List (provide JSON array)' },
        { name: 'Set', value: 'set', description: 'Set (provide JSON array)' },
      ],
      description: 'The type of value to store',
      displayOptions: {
        show: {
          operation: ['set'],
        },
      },
    },
    {
      key: 'valueIsJson',
      label: 'Value is JSON',
      type: 'boolean',
      default: false,
      description: 'Parse the value as JSON before storing (required for hash, list, set types)',
      displayOptions: {
        show: {
          operation: ['set'],
          keyType: ['hash', 'list', 'set'],
        },
      },
    },
    {
      key: 'expire',
      label: 'Set Expiry',
      type: 'boolean',
      default: false,
      description: 'Set an expiration time for this key',
      displayOptions: {
        show: {
          operation: ['set', 'increment'],
        },
      },
    },
    {
      key: 'ttl',
      label: 'TTL (seconds)',
      type: 'number',
      default: 60,
      description: 'Time to live in seconds',
      displayOptions: {
        show: {
          operation: ['set', 'increment'],
          expire: [true],
        },
      },
      typeOptions: {
        minValue: 1,
      },
      validation: [{ type: 'min', value: 1, message: 'TTL must be at least 1 second' }],
    },

    // ==================================================================
    // INCREMENT OPERATION
    // ==================================================================
    {
      key: 'key',
      label: 'Key',
      type: 'string',
      required: true,
      placeholder: 'counter:visits',
      description: "The key to increment (will be created if it doesn't exist)",
      displayOptions: {
        show: {
          operation: ['increment'],
        },
      },
      validation: [{ type: 'required', message: 'Key is required' }],
    },

    // ==================================================================
    // KEYS OPERATION
    // ==================================================================
    {
      key: 'pattern',
      label: 'Key Pattern',
      type: 'string',
      default: '*',
      placeholder: 'user:*',
      description: 'Pattern to match keys (supports * and ? wildcards)',
      displayOptions: {
        show: {
          operation: ['keys'],
        },
      },
    },
    {
      key: 'getValues',
      label: 'Get Values',
      type: 'boolean',
      default: false,
      description: 'Also retrieve the values for each matching key',
      displayOptions: {
        show: {
          operation: ['keys'],
        },
      },
    },

    // ==================================================================
    // PUSH OPERATION
    // ==================================================================
    {
      key: 'list',
      label: 'List',
      type: 'string',
      required: true,
      placeholder: 'queue:jobs',
      description: 'The name of the list to push to',
      displayOptions: {
        show: {
          operation: ['push'],
        },
      },
      validation: [{ type: 'required', message: 'List name is required' }],
    },
    {
      key: 'messageData',
      label: 'Data',
      type: 'string',
      required: true,
      placeholder: '{{ $node["NodeName"].object }}',
      description: 'The data to push to the list',
      displayOptions: {
        show: {
          operation: ['push'],
        },
      },
      typeOptions: {
        rows: 4,
      },
    },
    {
      key: 'tail',
      label: 'Push to Tail',
      type: 'boolean',
      default: true,
      description: 'Push to the end of the list (RPUSH). Disable to push to the beginning (LPUSH).',
      displayOptions: {
        show: {
          operation: ['push'],
        },
      },
    },

    // ==================================================================
    // POP OPERATION
    // ==================================================================
    {
      key: 'list',
      label: 'List',
      type: 'string',
      required: true,
      placeholder: 'queue:jobs',
      description: 'The name of the list to pop from',
      displayOptions: {
        show: {
          operation: ['pop'],
        },
      },
      validation: [{ type: 'required', message: 'List name is required' }],
    },
    {
      key: 'tail',
      label: 'Pop from Tail',
      type: 'boolean',
      default: true,
      description: 'Pop from the end of the list (RPOP). Disable to pop from the beginning (LPOP).',
      displayOptions: {
        show: {
          operation: ['pop'],
        },
      },
    },

    // ==================================================================
    // LIST LENGTH OPERATION
    // ==================================================================
    {
      key: 'list',
      label: 'List',
      type: 'string',
      required: true,
      placeholder: 'queue:jobs',
      description: 'The name of the list',
      displayOptions: {
        show: {
          operation: ['listLength'],
        },
      },
      validation: [{ type: 'required', message: 'List name is required' }],
    },

    // ==================================================================
    // PUBLISH OPERATION
    // ==================================================================
    {
      key: 'channel',
      label: 'Channel',
      type: 'string',
      required: true,
      placeholder: 'notifications',
      description: 'The channel to publish to',
      displayOptions: {
        show: {
          operation: ['publish'],
        },
      },
      validation: [{ type: 'required', message: 'Channel is required' }],
    },
    {
      key: 'messageData',
      label: 'Message',
      type: 'string',
      required: true,
      placeholder: '{{ $node["NodeName"].object }}',
      description: 'The message to publish',
      displayOptions: {
        show: {
          operation: ['publish'],
        },
      },
      typeOptions: {
        rows: 4,
      },
    },

    // ==================================================================
    // BULK SET OPERATION
    // ==================================================================
    {
      key: 'sourceArray',
      label: 'Source Array',
      type: 'string',
      required: true,
      placeholder: '{{ $node["NodeName"].array }}',
      description: 'Drag an array from a previous node. Each item should have "key" and "value" properties.',
      displayOptions: {
        show: {
          operation: ['bulkSet'],
        },
      },
    },
    {
      key: 'keyField',
      label: 'Key Field',
      type: 'string',
      default: 'key',
      placeholder: 'key',
      description: 'The field in each array item that contains the Redis key',
      displayOptions: {
        show: {
          operation: ['bulkSet'],
        },
      },
    },
    {
      key: 'valueField',
      label: 'Value Field',
      type: 'string',
      default: 'value',
      placeholder: 'value',
      description: 'The field in each array item that contains the value to set',
      displayOptions: {
        show: {
          operation: ['bulkSet'],
        },
      },
    },

    // ==================================================================
    // MULTI GET OPERATION
    // ==================================================================
    {
      key: 'keysToGet',
      label: 'Keys',
      type: 'string',
      required: true,
      placeholder: '{{ $node["NodeName"].array }}',
      description: 'Comma-separated list of keys or an array expression',
      displayOptions: {
        show: {
          operation: ['multiGet'],
        },
      },
    },

    // ==================================================================
    // KEY EXISTS OPERATION
    // ==================================================================
    {
      key: 'key',
      label: 'Key',
      type: 'string',
      required: true,
      placeholder: 'user:123',
      description: 'The key to check',
      displayOptions: {
        show: {
          operation: ['keyExists'],
        },
      },
    },

    // ==================================================================
    // FLUSH DATABASE OPTIONS
    // ==================================================================
    {
      key: 'confirmFlush',
      label: 'Confirm Flush',
      type: 'boolean',
      default: false,
      description: 'Confirm that you want to delete all keys in the database. This cannot be undone!',
      displayOptions: {
        show: {
          operation: ['flushDb'],
        },
      },
    },

    // ==================================================================
    // SETTINGS TAB
    // ==================================================================
    {
      key: 'continueOnError',
      label: 'Continue on Error',
      type: 'boolean',
      section: 'settings',
      default: false,
      description: 'Continue workflow execution even if this operation fails',
    },
  ],
};

export default schema;
